import { CheckIcon, PencilIcon } from '@heroicons/react/24/solid';
import { useReactFlow } from '@xyflow/react';
import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/button';
import { CloseIon } from 'src/components/icons/CloseIcon';
import IPInput from 'src/components/ip-input';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { dispatchNodesChanged } from 'src/utils/dispatch-nodes-changed';
import { calculateNetworkAddress2 } from 'src/utils/ip';
import { getErrorIpSubnet } from 'src/utils/validate';
import { twMerge } from 'tailwind-merge';

import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from '../network-configuration/models/node-data';

const NetworkIpSubnet = () => {
  const { globalIpSubnet, setGlobalIpSubnet } = useGlobalContext();
  const { setNodes, getNodes } = useReactFlow();

  const [isEditing, setIsEditing] = useState(false);

  const [editGlobalIpSubnet, setEditGlobalIpSubnet] = useState({
    ip: '',
    subnetMask: '',
  });

  const [errorMessage, setErrorMessage] = useState('');

  const handleValidation = (ip: string, subnetMask: string) => {
    const validateIpSubnetResult = getErrorIpSubnet({
      ip: ip || '',
      subnet: '',
      subnetMask: subnetMask || '',
      isCheckBroadcastAddress: false,
    });

    return (
      validateIpSubnetResult.ip ||
      validateIpSubnetResult.subnet ||
      validateIpSubnetResult.subnetMask
    );
  };

  const handleSave = () => {
    const networkAddress = calculateNetworkAddress2(
      editGlobalIpSubnet.ip,
      editGlobalIpSubnet.subnetMask,
    );

    if (networkAddress) {
      setGlobalIpSubnet({
        ip: editGlobalIpSubnet.ip,
        ipSubnet: networkAddress,
        subnetMask: editGlobalIpSubnet.subnetMask,
      });
      const managedIds = getNodes()
        .filter(
          (node) =>
            (node.data?.category as NodeCategoryModel)?.switchType?.includes(
              EDataSwitchType.MANAGED,
            ) ||
            (node.data?.category as NodeCategoryModel)?.type ===
              EDataType.Device,
        )
        .map((n) => n.id);
      let nodeTargetIndex = 0;
      setNodes((nodes) =>
        nodes.map((node) => {
          const nodeCategory = node.data?.category as NodeCategoryModel;
          const isManaged =
            nodeCategory?.switchType?.includes(EDataSwitchType.MANAGED) ||
            nodeCategory?.type === EDataType.Device;
          if (!isManaged) return node;
          nodeTargetIndex += 1;
          if (nodeTargetIndex > 255) {
            nodeTargetIndex = 1;
          }
          const newIp = `${networkAddress
            ?.split('.')
            .slice(0, 3)
            .join('.')}.${nodeTargetIndex}`;

          const newCategory = {
            ...nodeCategory,
            ip: newIp,
            subnet: networkAddress,
            subnetMask: editGlobalIpSubnet.subnetMask,
          };

          return {
            ...node,
            data: {
              ...node.data,
              category: newCategory,
            },
          };
        }),
      );
      dispatchNodesChanged(managedIds);

      setIsEditing(false);
    } else {
      setErrorMessage('Invalid IP Address or subnet mask');
    }
  };

  useEffect(() => {
    setEditGlobalIpSubnet({
      ip: globalIpSubnet?.ipSubnet ?? '',
      subnetMask: globalIpSubnet?.subnetMask ?? '',
    });
  }, [globalIpSubnet]);

  const isDisabled = !!errorMessage;

  if (!isEditing) {
    return (
      <div
        data-testid="global-ip-subnet-display"
        className="flex w-full flex-row items-center justify-between gap-2 px-2"
      >
        <div>
          {globalIpSubnet?.ipSubnet || '--'} /{' '}
          {globalIpSubnet?.subnetMask || '--'}
        </div>
        <Button
          name="edit"
          className="border border-gray-900 bg-white text-gray-900 hover:bg-blue-400 hover:text-white data-[hover]:bg-blue-400"
          onClick={() => setIsEditing(true)}
        >
          <PencilIcon className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="global-ip-subnet-edit"
      className="flex w-full flex-col items-start justify-start gap-2 px-2"
    >
      <div className="flex w-full flex-row items-start justify-start gap-2">
        <IPInput
          id="global-ip"
          name="globalIp"
          wrapperClassName="w-auto"
          placeholder="192.168.1.0"
          value={editGlobalIpSubnet.ip}
          className={twMerge([errorMessage ? 'border-red-500' : ''])}
          onChange={(value) => {
            setEditGlobalIpSubnet((prev) => ({
              ...prev,
              ip: value,
            }));
            setErrorMessage(
              handleValidation(value, editGlobalIpSubnet.subnetMask),
            );
          }}
          dataTestId="global-ip-subnet-ip-input"
        />
        <span className="mt-2 text-gray-500">/</span>
        <IPInput
          id="global-netmask"
          name="globalNetmask"
          wrapperClassName="w-auto"
          placeholder="255.255.255.0"
          value={editGlobalIpSubnet.subnetMask}
          onChange={(value) => {
            setEditGlobalIpSubnet((prev) => ({
              ...prev,
              subnetMask: value,
            }));
            setErrorMessage(handleValidation(editGlobalIpSubnet.ip, value));
          }}
          dataTestId="global-ip-subnet-subnet-mask-input"
        />
        <div className="flex gap-2">
          <Button
            data-testid="global-ip-subnet-cancel-button"
            name="cancel"
            color="red"
            onClick={() => setIsEditing(false)}
          >
            <CloseIon className="h-4 w-4" />
          </Button>
          <Button
            name="save"
            color="green"
            disabled={isDisabled}
            data-testid="global-ip-subnet-save-button"
            onClick={(e) => {
              if (isDisabled) return;
              handleSave();
            }}
          >
            <CheckIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {errorMessage && (
        <div className="text-sm text-red-500">{errorMessage}</div>
      )}
    </div>
  );
};

export default NetworkIpSubnet;
